/**
 * The Institution assignment a student is working on in a Study tool (SIR-TEA-01).
 *
 * The assignment page sets it after the student has started the assignment, then opens the tool.
 * Tool event senders (`studyToolSession`, `biologyDirect`) ask `assignmentIdForToolEvent` and add
 * `assignment_id` only to events of that exact subject and tool. Every other event stays private
 * self-study (no assignment id), so staff never see it (Q-SIR-06).
 *
 * In memory only: a reload or a different student clears it, and nothing is written to storage.
 *
 * The context lives only while the student stays on the assigned tool's route, entered from the
 * assignment page (router state `STUDY_ASSIGNMENT_NAV_STATE_KEY`). Leaving that route, opening the
 * same tool later from the tools hub, leaving Learn, or a closed/finished assignment clears it, so
 * later self-study in that tool stays private (SIR-VIS-04).
 */

import { ApiError } from '@/lib/api';

export interface ActiveStudyAssignment {
  assignment_id: string;
  subject_key: string;
  /** Catalogue id, as the assignment stores it. */
  tool_key: string;
  /** Kid principal that started it; another signed-in kid never inherits it. */
  kid_id: string;
  /** App route of the tool. The context is dropped when the student leaves it. */
  tool_path?: string;
}

/** Router state flag the assignment page sets when it opens the tool. */
export const STUDY_ASSIGNMENT_NAV_STATE_KEY = 'studyAssignmentId';

/**
 * Server refusals that concern only the assignment link (closed, not started, not found, wrong
 * target). The work itself is fine: senders drop the link and save it as private self-study.
 */
const ASSIGNMENT_LINK_ERROR_CODES: ReadonlySet<string> = new Set([
  'STUDY_ASSIGNMENT_CLOSED',
  'STUDY_ASSIGNMENT_NOT_STARTED',
  'STUDY_ASSIGNMENT_NOT_FOUND',
  'STUDY_ASSIGNMENT_TARGET_MISMATCH',
]);

export function isAssignmentLinkError(error: unknown): boolean {
  return error instanceof ApiError && ASSIGNMENT_LINK_ERROR_CODES.has(error.code ?? '');
}

/** Clears itself after this long so an old tab does not keep tagging events. */
const ACTIVE_FOR_MS = 3 * 60 * 60 * 1000;

/**
 * Biology tool clients report short event keys; Institution data uses catalogue ids. Keep in step
 * with the server map `STUDY_TOOL_EVENT_KEY_ALIASES` (platform-backend study-tool-catalog.ts).
 */
const EVENT_KEY_ALIASES: Readonly<Record<string, Readonly<Record<string, string>>>> = {
  biology: {
    cells: 'cell-explorer',
    protein: 'protein-factory',
    division: 'cell-division',
    membrane: 'membrane-transport',
    energy: 'cell-energy',
    mutation: 'gene-expression',
    enzyme: 'enzyme-lab',
    immune: 'immune-defence',
    biotech: 'biotechnology-lab',
    growth: 'growth-lab',
    ecosystem: 'ecosystems',
    body: 'body-systems',
    classification: 'classification-keys',
    microscope: 'virtual-microscope',
    transport: 'plant-transport-lab',
    sampling: 'field-sampling-lab',
  },
};

let active: (ActiveStudyAssignment & { expires_at: number; armed: boolean }) | null = null;

export function canonicalToolKey(subjectKey: string, toolKey: string): string {
  const alias = EVENT_KEY_ALIASES[subjectKey]?.[toolKey];
  if (alias) return alias;
  const prefix = `${subjectKey}-`;
  return toolKey.startsWith(prefix) ? toolKey.slice(prefix.length) : toolKey;
}

export function setActiveStudyAssignment(value: ActiveStudyAssignment, now = Date.now()): void {
  // Without a tool route there is nothing to arm from navigation (tests and direct callers).
  active = { ...value, expires_at: now + ACTIVE_FOR_MS, armed: !value.tool_path };
}

/**
 * Called on every Learn route change. The first visit to the tool route must carry the router
 * state flag for this assignment; after that the context stays while the route stays inside the
 * tool. Any other route clears it.
 */
export function syncActiveStudyAssignmentWithRoute(pathname: string, navState: unknown): void {
  if (!active?.tool_path) return;
  const path = active.tool_path;
  const onTool = pathname === path || pathname.startsWith(`${path}/`);
  if (!onTool) {
    active = null;
    return;
  }
  if (active.armed) return;
  const flag =
    typeof navState === 'object' && navState !== null
      ? (navState as Record<string, unknown>)[STUDY_ASSIGNMENT_NAV_STATE_KEY]
      : undefined;
  if (flag === active.assignment_id) active.armed = true;
  else active = null;
}

/** Clears the context when this assignment is finished or no longer open. */
export function clearStudyAssignmentIfDone(assignment: {
  id: string;
  status: string;
  assignment_status: string;
}): void {
  if (!active || active.assignment_id !== assignment.id) return;
  if (assignment.status === 'completed' || assignment.assignment_status !== 'published') {
    active = null;
  }
}

export function clearActiveStudyAssignment(): void {
  active = null;
}

export function activeStudyAssignment(now = Date.now()): ActiveStudyAssignment | null {
  if (active && active.expires_at <= now) active = null;
  return active;
}

/** The assignment id for this tool event, or undefined for private self-study. */
export function assignmentIdForToolEvent(
  kidId: string | null,
  subjectKey: string,
  toolKey: string,
  now = Date.now(),
): string | undefined {
  const current = activeStudyAssignment(now);
  if (!current || !active?.armed || !kidId || current.kid_id !== kidId) return undefined;
  if (current.subject_key !== subjectKey) return undefined;
  const key = canonicalToolKey(subjectKey, toolKey);
  return key === current.tool_key || toolKey === current.tool_key
    ? current.assignment_id
    : undefined;
}
